import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Search, Upload, Download, ChevronDown, ChevronUp, Trash2, Loader2, RefreshCw, ArrowLeft, Clock } from 'lucide-react';
import SidebarLayout from './components/layout/SidebarLayout';
import AnalysisDetail from './components/ui/AnalysisDetail';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import CreditsPage from './components/billing/CreditsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import Admin from './components/admin/Admin';
import AdminAnalysisDetail from './components/admin/AdminAnalysisDetail';
import Profile from './components/auth/Profile';
import { useCredits } from './contexts/CreditsContext';
import './fonts.css';
import SharedAnalyses from './components/analysis/SharedAnalyses';
import SharedAnalysisDetail from './components/analysis/SharedAnalysisDetail';
import EventsList from './components/events/EventsList';
import EventDetail from './components/events/EventDetail';
import { API_BASE_URL } from './config/api';

// Define dance types
const DANCE_TYPES = [
  // Ballroom Dances
  'AMERICAN SMOOTH WALTZ',
  'AMERICAN SMOOTH TANGO',
  'AMERICAN SMOOTH VIENNESE WALTZ',
  'AMERICAN SMOOTH FOX TROT',
  'INTERNATIONAL WALTZ', 
  'INTERNATIONAL TANGO', 
  'INTERNATIONAL VIENNESE WALTZ', 
  'INTERNATIONAL SLOW FOX TROT', 
  'INTERNATIONAL QUICKSTEP',
  
  // Latin Dances
  'INTERNATIONAL SAMBA', 
  'INTERNATIONAL CHA CHA CHA', 
  'INTERNATIONAL RUMBA', 
  'INTERNATIONAL PASO DOBLE', 
  'INTERNATIONAL JIVE',
  
  // Duo Dances (new)
  'CONTEMPORARY',
  'KRUMPING',
  'CROSSDANCE',
  'TRADITIONAL',
  'HIP HOP',
  'BACHATA',
  'SALSA',
  'FREESTYLE',
  'MAMBO',
  'AFROBEAT',
  'BALLET',

  // Formation Dances
  'FORMATION STANDARD',
  'FORMATION LATIN',
  'FORMATION MIXED',
  'FORMATION THEATRICAL',
  'FORMATION TRADITIONAL'
  
];

// Private route component for protected routes
const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Main dashboard component
const Dashboard = () => {
  const { currentUser, getIdToken } = useAuth();
  const { credits, fetchUserCredits } = useCredits();
  const location = useLocation();
  //const [searchParams] = useSearchParams();
  //const typeParam = searchParams.get('type');

  const [isAdmin, setIsAdmin] = useState(false);

  const getDanceTypesByAnalysisType = (type = analysisType) => {
    if (type === 'couple') {
      return DANCE_TYPES.slice(0, 24);
    } else if (type === 'solo') {
      return DANCE_TYPES.slice(0, 24);
    } else if (type === 'formation') {
      return DANCE_TYPES.slice(25, 30);
    } else { // duo
      return DANCE_TYPES.slice(0, 24);
    }
  };
  
  // State for analyses
  const [analyses, setAnalyses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingAnalysisId, setDeletingAnalysisId] = useState(null);

  // State for file upload form
  const [maleDancerName, setMaleDancerName] = useState('');
  const [femaleDancerName, setFemaleDancerName] = useState('');
  const [selectedDanceType, setSelectedDanceType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // State for drag-and-drop
  const [isDragging, setIsDragging] = useState(false);

  // State for analysis type - get from URL param or default to 'couple'
  const [analysisType, setAnalysisType] = useState('couple');

  // Update analysisType when URL param changes
  // useEffect(() => {
  //   setAnalysisType(
  //     typeParam === 'solo' ? 'solo' : 
  //     typeParam === 'duo' ? 'duo' : 
  //     typeParam === 'formation' ? 'formation' : 'couple'
  //   );
  // }, [typeParam]);

  useEffect(() => {
    // Reset selected dance type when analysis type changes
    setSelectedDanceType('');
  }, [analysisType]);

  // Check if there are any processing/pending analyses
  const hasProcessingAnalyses = () => {
    return analyses.some(analysis =>
      analysis.status === 'processing' ||
      analysis.status === 'Analysis in progress..' ||
      !analysis.processed
    );
  };

  useEffect(() => {
    fetchAnalyses();
    ensureUserDocument();
  }, [analysisType]); // Re-fetch when analysis type changes

  // Refresh when returning from detail page (location change to dashboard)
  useEffect(() => {
    // If we're on the main dashboard page (not a detail page), refresh data
    if (!selectedAnalysis && (location.pathname === '/' || location.pathname.startsWith('/couple') || location.pathname.startsWith('/solo') || location.pathname.startsWith('/duo') || location.pathname.startsWith('/formation'))) {
      fetchAnalyses();
    }
  }, [location]);

  // Polling effect - only runs when there are processing/pending analyses
  useEffect(() => {
    if (hasProcessingAnalyses()) {
      const interval = setInterval(fetchAnalyses, 30000); // Poll every 30 seconds when processing
      return () => clearInterval(interval);
    }
  }, [analyses]); // Re-evaluate when analyses change

  // Background polling - always runs every 60 seconds to catch admin-initiated reruns
  useEffect(() => {
    const interval = setInterval(fetchAnalyses, 60000); // Poll every 60 seconds always
    return () => clearInterval(interval);
  }, []);

  // Refresh when tab becomes visible (user switched from admin tab back to main tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !selectedAnalysis) {
        // Only refresh if we're on the main list (not viewing detail)
        fetchAnalyses();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedAnalysis]);

  useEffect(() => {
    const checkAdmin = () => {
      if (currentUser) {
        // List of admin UIDs - same as in SidebarLayout
        const ADMIN_UIDS = [
          "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
          // Add other admin UIDs here
        ];
        
        setIsAdmin(ADMIN_UIDS.includes(currentUser.uid));
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdmin();
  }, [currentUser]);

  // Function to ensure user has a document in Firestore
  const ensureUserDocument = async () => {
    if (!currentUser) return;
    
    try {
      const token = await getIdToken();
      if (!token) return;
      
      // Verify token which will create the document if it doesn't exist
      const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: token
        }),
      });
      
      if (!response.ok) {
        console.error('Failed to verify token and ensure user document');
      }
    } catch (error) {
      console.error('Error ensuring user document:', error);
    }
  };

  

  const handleRerunAnalysis = async (analysisId) => {
    console.log('=== handleRerunAnalysis called from list ===');
    console.log('Analysis ID:', analysisId);

    // Immediately update the local state to show processing
    console.log('Setting local state to processing...');
    updateAnalysisInList(analysisId, {
      processed: false,
      status: 'processing'
    });

    try {
      setLoading(true);
      setError(null);

      // Get token
      const token = await getIdToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      console.log('Calling rerun API...');
      const response = await fetch(`${API_BASE_URL}/analyses/${analysisId}/rerun`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Rerun API response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to rerun analysis');
      }

      // Show success message
      alert('Analysis rerun started successfully. This may take several minutes to complete.');

      // Refresh the analyses list in the background to get any other updates
      fetchAnalyses().catch(err => console.error('Background refresh failed:', err));

      console.log('=== handleRerunAnalysis complete ===');
    } catch (error) {
      console.error('Rerun error:', error);
      setError(`Failed to rerun analysis: ${error.message}`);
      // If there was an error, revert the status
      updateAnalysisInList(analysisId, {
        processed: true,
        status: 'completed'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyses = async () => {
    try {
      // Get authentication token if user is logged in
      let headers = { 
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
      
      if (currentUser) {
        const token = await getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      // Build URL with parameters - always include analysis_type filter
      let url = `${API_BASE_URL}/analyses?analysis_type=${analysisType}`;
      
      // Add user_id filter if logged in
      if (currentUser) {
        url += `&user_id=${currentUser.uid}`;
      }
      
      const response = await fetch(url, {
        headers: headers,
        mode: 'cors'
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      
      // Additional client-side filtering to ensure we only show the correct type
      const filteredData = data.filter(analysis => 
        analysis.analysis_type === analysisType || 
        // For backward compatibility with old records that might not have analysis_type
        (analysisType === 'couple' && !analysis.analysis_type)
      );

      setAnalyses(filteredData);

      // Update selectedAnalysis if it's in the list and has new data
      if (selectedAnalysis) {
        const updatedAnalysis = filteredData.find(a => a.id === selectedAnalysis.id);
        if (updatedAnalysis) {
          setSelectedAnalysis(updatedAnalysis);
        }
      }

      setError(null);
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load analyses');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes

    if (file && file.size > maxSize) {
      setError('File size must be less than 100MB');
      setSelectedFile(null);
      // Reset the file input
      event.target.value = '';
      return;
    }

    if (file && !file.type.startsWith('video/')) {
      setError('Please select a video file');
      setSelectedFile(null);
      event.target.value = '';
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate the dropped file
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        setError('File size must be less than 100MB');
        setSelectedFile(null);
        return;
      }
      if (!file.type.startsWith('video/')) {
        setError('Please select a video file');
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
    }
  };

  const submitAnalysis = async () => {
    // Validate common inputs
    if (analysisType === 'couple') {
      if (!maleDancerName.trim()) {
        setError('Please enter the male dancer\'s name');
        return;
      }
      if (!femaleDancerName.trim()) {
        setError('Please enter the female dancer\'s name');
        return;
      }
    } else { // solo
      if (!maleDancerName.trim()) {
        setError('Please enter the dancer\'s name');
        return;
      }
    }
    
    if (!selectedDanceType) {
      setError('Please select a dance type');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);
      setInsufficientCredits(false);

      // Get authentication token
      let headers = {};
      if (currentUser) {
        const token = await getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Validate file upload
      if (!selectedFile) {
        setError('Please select a video file');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('video', selectedFile);

      // Format dancers differently based on analysis type
      if (analysisType === 'couple') {
        formData.append('dancers', `Man: ${maleDancerName}, Lady: ${femaleDancerName}`);
      } else if (analysisType === 'solo') {
        formData.append('dancers', `Dancer: ${maleDancerName}`);
      } else if (analysisType === 'formation') {
        formData.append('dancers', `Formation: ${maleDancerName}`);
      } else {
        formData.append('dancers', `Duo: ${maleDancerName}`);
      }

      // Explicitly set analysis_type
      formData.append('dance_type', selectedDanceType);
      formData.append('analysis_type', analysisType);

      console.log(`Submitting ${analysisType} analysis via file upload`);


      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 200 || xhr.status === 202) {
          // 200 = synchronous processing complete, 202 = queued for async processing
          const response = JSON.parse(xhr.responseText);
          resetForm();
          await fetchAnalyses();
        } else if (xhr.status === 400) {
          // Check for insufficient credits error
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.error && response.error.includes("Insufficient credits")) {
              setInsufficientCredits(true);
            } else {
              setError(response.error || 'Upload failed');
            }
          } catch (e) {
            setError('Upload failed');
          }
        } else {
          throw new Error('Upload failed');
        }
        setLoading(false);
      };

      xhr.onerror = () => {
        setError('Upload failed');
        setLoading(false);
      };

      xhr.open('POST', `${API_BASE_URL}/analyses/analyze`, true);

      // Add authorization header if available
      if (headers['Authorization']) {
        xhr.setRequestHeader('Authorization', headers['Authorization']);
      }

      xhr.send(formData);
    } catch (error) {
      setError(error.message || 'Upload failed');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMaleDancerName('');
    setFemaleDancerName('');
    setSelectedDanceType('');
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this analysis?')) return;

    try {
      setDeletingAnalysisId(id);
      setError(null);

      // Get auth token if available
      let headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };

      if (currentUser) {
        const token = await getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/analyses/${id}`, {
        method: 'DELETE',
        headers: headers,
        mode: 'cors'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      // Clear selected analysis if it was the deleted one
      if (selectedAnalysis && selectedAnalysis.id === id) {
        setSelectedAnalysis(null);
      }

      await fetchAnalyses();
    } catch (error) {
      setError('Delete failed: ' + error.message);
    } finally {
      setDeletingAnalysisId(null);
    }
  };

  // Helper function to update a specific analysis in the analyses list
  const updateAnalysisInList = (analysisId, updates) => {
    console.log('=== updateAnalysisInList called ===');
    console.log('analysisId:', analysisId);
    console.log('updates:', updates);
    console.log('Current analyses count:', analyses.length);

    setAnalyses(prevAnalyses => {
      const updated = prevAnalyses.map(analysis =>
        analysis.id === analysisId
          ? { ...analysis, ...updates }
          : analysis
      );
      console.log('Updated analyses count:', updated.length);
      console.log('Updated analysis:', updated.find(a => a.id === analysisId));
      return updated;
    });

    // Also update selectedAnalysis if it's the same one
    if (selectedAnalysis && selectedAnalysis.id === analysisId) {
      console.log('Also updating selectedAnalysis');
      setSelectedAnalysis(prev => {
        const updated = prev ? { ...prev, ...updates } : null;
        console.log('Updated selectedAnalysis:', updated);
        return updated;
      });
    }

    console.log('=== updateAnalysisInList complete ===');
  };

  const formatDate = (timestamp) => {
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(8, 10);
    const minute = timestamp.substring(10, 12);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const getStatusStyle = (status, processed) => {
    console.log(`getStatusStyle called: status="${status}", processed=${processed}`);
    if (status === 'processing' || status === 'Analysis in progress..') {
      console.log('  -> Returning ORANGE (processing)');
      return 'bg-orange-100 border-l-4 border-orange-500';
    }
    if (!processed) {
      console.log('  -> Returning YELLOW (pending)');
      return 'bg-yellow-50 border-l-4 border-yellow-500';
    }
    console.log('  -> Returning GREEN (completed)');
    return 'bg-green-50 border-l-4 border-green-500';
  };

  const filteredAndSortedAnalyses = analyses
    .filter(analysis => 
      analysis.dance_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.dancers?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      return sortOrder === 'desc' 
        ? b.timestamp.localeCompare(a.timestamp)
        : a.timestamp.localeCompare(b.timestamp);
    });

  if (selectedAnalysis) {
    return (
      <AnalysisDetail
        analysis={selectedAnalysis}
        onBack={() => setSelectedAnalysis(null)}
        onAnalysisUpdate={setSelectedAnalysis}
        onDelete={handleDelete}
        isDeleting={deletingAnalysisId === selectedAnalysis.id}
        onRefreshAnalyses={fetchAnalyses}
        onUpdateAnalysisInList={updateAnalysisInList}
      />
    );
  }

  // Helper function to extract scores from analysis text
  const extractScores = (text) => {
    if (!text) return null;
    
    // First, try to find a direct TOTAL SCORE
    const totalScoreRegex = /TOTAL\s+SCORE:?\s*(?:=\s*)?(\d+\.?\d*)/i;
    const totalMatch = text.match(totalScoreRegex);
    
    if (totalMatch && totalMatch[1]) {
      return (
        <span className="font-medium text-green-600">
          Total Score: {parseFloat(totalMatch[1]).toFixed(1)}
        </span>
      );
    }
    
    // If no direct total score, try to find component scores
    const scores = {};
    const patterns = {
      mm: /Movement to Music \(MM\):?\s*(?:=\s*)?(\d+\.?\d*)/i,
      tq: /Technical Quality \(TQ\):?\s*(?:=\s*)?(\d+\.?\d*)/i,
      cp: /Choreography & Presentation \(CP\):?\s*(?:=\s*)?(\d+\.?\d*)/i,
      ps: /Partnering Skills \(PS\):?\s*(?:=\s*)?(\d+\.?\d*)/i,
      
      // Add patterns for other types of scores (solo, duo, formation)
      technique: /Technique:?\s*(?:=\s*)?(\d+\.?\d*)/i,
      musicality: /Musicality:?\s*(?:=\s*)?(\d+\.?\d*)/i,
      expression: /Expression:?\s*(?:=\s*)?(\d+\.?\d*)/i,
      synchronization: /Synchronization:?\s*(?:=\s*)?(\d+\.?\d*)/i,
      teamwork: /Teamwork:?\s*(?:=\s*)?(\d+\.?\d*)/i,
      choreography: /Choreography:?\s*(?:=\s*)?(\d+\.?\d*)/i
    };
    
    // Try to extract each score
    for (const [key, pattern] of Object.entries(patterns)) {
      const match = text.match(pattern);
      if (match && match[1]) {
        scores[key] = parseFloat(match[1]).toFixed(1);
      }
    }
    
    if (Object.keys(scores).length === 0) return null;
    
    // Calculate total if it doesn't exist but we have component scores
    let calculatedTotal = 0;
    let scoreCount = 0;
    
    for (const score of Object.values(scores)) {
      calculatedTotal += parseFloat(score);
      scoreCount++;
    }
    
    // Format the extracted scores
    return (
      <>
        {scoreCount > 0 && (
          <span className="font-medium text-green-600 mr-2">
            Total Score: {(calculatedTotal / scoreCount).toFixed(1)}
          </span>
        )}
        {scores.mm && `MM: ${scores.mm}`}{' '}
        {scores.tq && `TQ: ${scores.tq}`}{' '}
        {scores.cp && `CP: ${scores.cp}`}{' '}
        {scores.ps && `PS: ${scores.ps}`}{' '}
        {scores.technique && `Technique: ${scores.technique}`}{' '}
        {scores.musicality && `Musicality: ${scores.musicality}`}{' '}
        {scores.expression && `Expression: ${scores.expression}`}{' '}
        {scores.synchronization && `Sync: ${scores.synchronization}`}{' '}
        {scores.teamwork && `Teamwork: ${scores.teamwork}`}{' '}
        {scores.choreography && `Choreography: ${scores.choreography}`}
      </>
    );
  };

  return (
    <div className="h-full">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {insufficientCredits && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-6">
          <div className="font-bold mb-1">Insufficient Credits</div>
          <p>You don't have enough credits to perform this analysis. Please purchase more credits.</p>
          <div className="mt-2">
            <Link 
              to="/credits" 
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Purchase Credits
            </Link>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
        {/* Analysis Type Indication */}
        <div className="mb-4 md:mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Select Analysis Type
          </label>
          <select
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            className="w-full p-3 text-base border rounded-lg bg-white min-h-[44px]"
          >
            <option value="couple">Couple Analysis</option>
            <option value="solo">Solo Analysis</option>
            <option value="duo">Duo Analysis</option>
            <option value="formation">Formation Analysis</option>
          </select>
        </div>

        {/* Analysis Type Description */}
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {analysisType === 'couple' ? 'Couple Dance Analysis' : 
            analysisType === 'solo' ? 'Solo Dance Analysis' : 
            analysisType === 'formation' ? 'Formation Dance Analysis' :
            'Duo Dance Analysis'}
          </h1>
          <p className="text-gray-600">
            {analysisType === 'couple' 
              ? 'Analyze a dance couple using Technical Quality, Movement to Music, Partnering Skill, Choreography & Presentation' 
              : analysisType === 'solo'
              ? 'Analyze an individual dancer focusing on Technique, Creativity'
              : analysisType === 'formation'
              ? 'Analyze a formation dance focusing on Synchronization, Choreography, and Team Performance'
              : 'Analyze a duo performance focusing on Synchronization, Technique and Creativity'}
          </p>
          <p className="text-sm text-blue-600 mt-1">
            Each analysis costs 5 credits.
          </p>
        </div>

        {/* Dancer Names - Conditional based on analysis type */}
        {analysisType === 'couple' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Male Dancer Name *
            </label>
            <input
              type="text"
              value={maleDancerName}
              onChange={(e) => setMaleDancerName(e.target.value)}
              className="w-full p-3 text-base border rounded-lg min-h-[44px]"
              placeholder="Enter male dancer's name"
              required
              disabled={loading}
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Female Dancer Name *
            </label>
            <input
              type="text"
              value={femaleDancerName}
              onChange={(e) => setFemaleDancerName(e.target.value)}
              className="w-full p-3 text-base border rounded-lg min-h-[44px]"
              placeholder="Enter female dancer's name"
              required
              disabled={loading}
            />
          </div>
        </div>
      ) : analysisType === 'solo' ? (
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Dancer Name *
          </label>
          <input
            type="text"
            value={maleDancerName}
            onChange={(e) => setMaleDancerName(e.target.value)}
            className="w-full p-3 text-base border rounded-lg min-h-[44px]"
            placeholder="Enter dancer's name"
            required
            disabled={loading}
          />
        </div>
      ) : analysisType === 'formation' ? (
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Formation Name *
          </label>
          <input
            type="text"
            value={maleDancerName}
            onChange={(e) => setMaleDancerName(e.target.value)}
            className="w-full p-3 text-base border rounded-lg min-h-[44px]"
            placeholder="Enter formation name"
            required
            disabled={loading}
          />
        </div>
      ) : (
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Duo Name *
          </label>
          <input
            type="text"
            value={maleDancerName}
            onChange={(e) => setMaleDancerName(e.target.value)}
            className="w-full p-3 text-base border rounded-lg min-h-[44px]"
            placeholder="Enter duo name"
            required
            disabled={loading}
          />
        </div>
      )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Dance Type *
            </label>
            <select
              value={selectedDanceType}
              onChange={(e) => setSelectedDanceType(e.target.value)}
              className="w-full p-3 text-base border rounded-lg min-h-[44px]"
              required
              disabled={loading}
            >
              <option value="">Select Dance Type</option>
              {getDanceTypesByAnalysisType(analysisType).map((dance) => (
                <option key={dance} value={dance}>{dance}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Video File *
          </label>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`relative flex items-center justify-center w-full p-4 border-2 rounded-lg transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-dashed border-gray-300 hover:border-blue-500'
            }`}
          >
            <input
              type="file"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept="video/mp4,video/quicktime"
              onChange={handleFileUpload}
              disabled={loading}
            />
            <div className="text-center pointer-events-none">
              <Upload className={`w-8 h-8 mb-2 mx-auto ${isDragging ? 'text-blue-500' : 'text-gray-500'}`} />
              <span className={`text-sm md:text-base ${isDragging ? 'text-blue-600' : 'text-gray-600'}`}>
                {selectedFile ? selectedFile.name : 'Drag & drop video here or tap to upload'}
              </span>
              <p className="text-xs text-gray-500 mt-1">MP4 or MOV, max 100MB</p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{width: `${uploadProgress}%`}}
              ></div>
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">
              {uploadProgress}% Uploaded
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <button
            onClick={submitAnalysis}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center min-h-[48px] text-base font-medium"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Uploading...
              </>
            ) : (
              'Submit Analysis'
            )}
          </button>
        </div>
      </div>

      {/* Analysis list header */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            {analysisType === 'couple' ? 'Couple Dance Analyses' :
            analysisType === 'solo' ? 'Solo Dance Analyses' :
            analysisType === 'formation' ? 'Formation Dance Analyses' :
            'Duo Dance Analyses'}
          </h2>
          {hasProcessingAnalyses() && (
            <div className="flex items-center gap-1 text-blue-600 text-sm font-medium">
              <Loader2 className="w-4 h-4 animate-spin" />
              Auto-refreshing...
            </div>
          )}
        </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 relative w-full">
            <input
              type="text"
              placeholder={`Search ${analysisType} analyses...`}
              className="w-full p-2 pl-10 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>
          <button
            onClick={() => setSortOrder(order => order === 'desc' ? 'asc' : 'desc')}
            className="px-4 py-2 border rounded-lg flex items-center gap-2 hover:bg-gray-50 whitespace-nowrap"
          >
            Date
            {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button
            onClick={async () => {
              setIsRefreshing(true);
              await fetchAnalyses();
              setTimeout(() => setIsRefreshing(false), 500);
            }}
            className="px-4 py-2 border rounded-lg flex items-center gap-2 hover:bg-gray-50 whitespace-nowrap"
            disabled={isRefreshing}
            title="Refresh analyses"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="grid grid-cols-1 p-4 font-semibold border-b">
          <div className="flex justify-between items-center">
            <div>Date</div>
            <div>Dance Type</div>
          </div>
        </div>

        

        {filteredAndSortedAnalyses.length > 0 ? (
          filteredAndSortedAnalyses.map(analysis => (
            <div
              key={analysis.id}
              onClick={() => setSelectedAnalysis(analysis)}
              className={`${getStatusStyle(analysis.status, analysis.processed)} border-b hover:bg-gray-50 cursor-pointer`}
            >
              <div className="grid grid-cols-1 p-4 gap-2">
                <div className="flex justify-between items-center">
                  <div>{formatDate(analysis.timestamp)}</div>
                  <div>{analysis.dance_type}</div>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  {analysis.status === 'processing' || analysis.status === 'Analysis in progress..' ? (
                    <div className="flex items-center gap-1 text-orange-700 text-sm font-medium">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </div>
                  ) : !analysis.processed ? (
                    <div className="flex items-center gap-1 text-yellow-700 text-sm font-medium">
                      <Clock className="w-4 h-4" />
                      Pending
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-green-700 text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Completed
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <div>
                    {analysis.analysis_type === 'solo' 
                      ? (analysis.dancers && analysis.dancers.includes('Dancer:') 
                        ? analysis.dancers 
                        : `Dancer: ${analysis.dancers.replace(/Man: |Lady: |Unknown, /g, '')}`)
                      : analysis.analysis_type === 'duo'
                        ? (analysis.dancers && analysis.dancers.includes('Duo:') 
                          ? analysis.dancers 
                          : `Duo: ${analysis.dancers.replace(/Man: |Lady: |Dancer: |Unknown, /g, '')}`)
                        : analysis.analysis_type === 'formation'
                          ? (analysis.dancers && analysis.dancers.includes('Formation:') 
                            ? analysis.dancers 
                            : `Formation: ${analysis.dancers.replace(/Man: |Lady: |Dancer: |Duo: |Unknown, /g, '')}`)
                          : analysis.dancers
                    }
                  </div>
                  
                  {/* Score Summary - Show for all analysis types if available */}
                  {analysis.processed && (
                    <div className="text-sm text-gray-600 mt-1">
                      {/* Show total score directly if it exists in the analysis object */}
                      {analysis.total_score && (
                        <span className="font-medium text-green-600">Total Score: {analysis.total_score}</span>
                      )}
                      
                      {/* If no direct total_score but has component scores, show those */}
                      {!analysis.total_score && analysis.scores && (
                        <>
                          {analysis.scores.mm && `MM: ${analysis.scores.mm}`}{' '}
                          {analysis.scores.tq && `TQ: ${analysis.scores.tq}`}{' '}
                          {analysis.scores.cp && `CP: ${analysis.scores.cp}`}{' '}
                          {analysis.scores.ps && `PS: ${analysis.scores.ps}`}
                        </>
                      )}
                      
                      {/* If no structured scores, try to extract from text */}
                      {!analysis.total_score && !analysis.scores && analysis.text && extractScores(analysis.text)}
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2 mt-1">
                    {(!analysis.processed || isAdmin) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRerunAnalysis(analysis.id);
                        }}
                        className="px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded flex items-center gap-1 text-sm min-h-[36px]"
                        title={isAdmin ? "Rerun analysis (admin)" : "Rerun stuck analysis"}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span className="hidden sm:inline">Rerun</span>
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(analysis.id);
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded flex items-center gap-1 text-sm min-h-[36px]"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            {analyses.length === 0 ? (
              `No ${analysisType} analyses found`
            ) : (
              'No matches found for your search'
            )}
            
          </div>

          
        )}
      </div>
       {/* Add Footer here */}
       <div className="w-full p-4 mt-8 border-t text-xs text-gray-500">
        <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0">
          <a
            href="https://novosense.africa/flokraft/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-600 transition-colors text-center sm:text-left"
          >
            Privacy Policy
          </a>
          <a
            href="https://novosense.africa/flokraft/deletion.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-600 transition-colors text-center sm:text-right"
          >
            Data Deletion
          </a>
        </div>
        <div className="mt-3 text-center">
          © {new Date().getFullYear()} Flokraft
        </div>
      </div>
      
    </div>
  );
};

// Main App component with routing
const App = () => {
  return (
    <AuthProvider>
      <CreditsProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Protected routes with sidebar layout */}
            <Route path="/" element={
              <PrivateRoute>
                <SidebarLayout>
                  <Dashboard />
                </SidebarLayout>
              </PrivateRoute>
            } />
            
            <Route path="/profile" element={
              <PrivateRoute>
                <SidebarLayout>
                  <Profile />
                </SidebarLayout>
              </PrivateRoute>
            } />
            
            <Route path="/credits" element={
              <PrivateRoute>
                <SidebarLayout>
                  <CreditsPage />
                </SidebarLayout>
              </PrivateRoute>
            } />
            
            <Route path="/admin" element={
              <PrivateRoute>
                <SidebarLayout>
                  <Admin />
                </SidebarLayout>
              </PrivateRoute>
            } />
            
            <Route path="/admin/analysis/:id" element={
              <PrivateRoute>
                <SidebarLayout>
                  <AdminAnalysisDetail />
                </SidebarLayout>
              </PrivateRoute>
            } />

            <Route path="/shared-analyses" element={
              <PrivateRoute>
                <SidebarLayout>
                  <SharedAnalyses />
                </SidebarLayout>
              </PrivateRoute>
            } />

            <Route path="/shared/:shareId" element={
              <PrivateRoute>
                <SidebarLayout>
                  <SharedAnalysisDetail />
                </SidebarLayout>
              </PrivateRoute>
            } />

            <Route path="/events" element={
              <PrivateRoute>
                <SidebarLayout>
                  <EventsList />
                </SidebarLayout>
              </PrivateRoute>
            } />

            <Route path="/events/:eventId" element={
              <PrivateRoute>
                <SidebarLayout>
                  <EventDetail />
                </SidebarLayout>
              </PrivateRoute>
            } />
          </Routes>
          
        </Router>
      </CreditsProvider>
    </AuthProvider>
  );
};

export default App;